import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../common/Button';
import AuthorSection from '../common/AuthorSection';

const HeroContainer = styled.section`
  background-color: #121212;
  background-image: linear-gradient(to bottom, rgba(18, 18, 18, 0.9), rgba(18, 18, 18, 1)), 
    url('/images/hero-bg.jpg');
  background-size: cover;
  background-position: center;
  color: #FFFFFF;
  padding: 120px 0;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const CarSilhouette = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0.1;
  background-image: url('/images/car-silhouette.svg');
  background-repeat: no-repeat;
  background-position: center bottom;
  background-size: contain;
  z-index: 1;
`;

const CircuitPattern = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0.05;
  background-image: url('/images/circuit-pattern.svg');
  background-repeat: repeat;
  background-size: 500px;
  z-index: 1;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
  
  text-align: center;
`;

const Title = styled(motion.h1)`
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 56px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const AnimatedText = styled(motion.span)`
  color: #2196F3;
  display: inline-block;
`;

const Subtitle = styled(motion.p)`
  font-family: 'Roboto', sans-serif;
  font-size: 24px;
  line-height: 1.5;
  color: #E0E0E0;
  margin-bottom: 40px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 16px;
  margin-bottom: 40px;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const SocialProof = styled(motion.div)`
  font-family: 'Roboto', sans-serif;
  font-size: 18px;
  color: #E0E0E0;
  margin-top: 40px;
  
  span {
    font-weight: 700;
  }
`;

const OfferBanner = styled(motion.div)`
  background-color: rgba(255, 215, 0, 0.15);
  border: 1px solid #FFD700;
  border-radius: 8px;
  padding: 16px 24px;
  margin-top: 40px;
  display: inline-block;
  
  p {
    color: #FFD700;
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    font-size: 18px;
    margin: 0;
  }
  
  @media (max-width: 768px) {
    p {
      font-size: 16px;
    }
  }
`;

const HeroContent = styled.div`
  @media (max-width: 991px) {
    margin-bottom: 60px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  
  img {
    max-width: 380px;
    height: auto;
  }
`;




const HeroSection = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const targetAudiences = [
    "First Buyers",
    "Fleet Managers",
    "Students",
    "Entrepreneurs",
    "Business Owners",
    "Industry Workers"
  ];
  
  // Rotate through target audiences every 2 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTextIndex((prevIndex) => (prevIndex + 1) % targetAudiences.length);
    }, 2000);
    
    return () => clearInterval(intervalId);
  }, [targetAudiences.length]);
  

  
  return (
    <HeroContainer id="home">
      <CarSilhouette />
      <CircuitPattern />
      <Content>
        <LogoContainer>
          <motion.img 
            src="/logo192.png" 
            alt="CarConsul Logo"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          />
        </LogoContainer>
        <HeroContent>
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Independent Car Advisor for
            <br />
            <AnimatePresence mode="wait">
              <AnimatedText
                key={currentTextIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {targetAudiences[currentTextIndex]}
              </AnimatedText>
            </AnimatePresence>
          </Title>
          
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Check Any Car Before Buying. Get Full Report. Compare ANY Vehicles. Master Negotiations and Save.
          </Subtitle>
          
          <ButtonGroup
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Button 
              variant="primary" 
              size="large"
              id="hero-cta-button"
              sectionName="hero"
              onClick={() => window.location.href = 'https://app.carconsul.com'}
            >
              Check My Car
            </Button>
          </ButtonGroup>
          
          <SocialProof
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            Car buyers trust us — and counting
          </SocialProof>
          
          <OfferBanner
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
          >
            <p>🔥 Only 37 spots this month! 🔥</p>
          </OfferBanner>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            style={{ marginTop: '40px', maxWidth: '400px', margin: '40px auto 0' }}
          >
            <AuthorSection 
              showDescription={false}
              showDMText={true}
              size="compact"
            />
          </motion.div>
        </HeroContent>
      </Content>
    </HeroContainer>
  );
};

export default HeroSection;
